import QRCode from "qrcode";
import { pdf } from "@react-pdf/renderer";
import TicketModel from "../components/pdf";
import { sendQRCode } from "./sendEmail";
import { db, storage } from "../firebase";
import { getDownloadURL, ref } from "firebase/storage";
import { PDFDocument, rgb } from "pdf-lib";
import { collection, getDocs } from "firebase/firestore";
import ENV from "../env";
import { splitTicketsForPdf } from "./ticketPdfPricing";
import { downloadPdfBlob } from "./downloadPdfBlob";

const TEXT_HEIGHT = 40;
const TEXT_FONT = 10;
const TEXT_LINE_HEIGHT = 12;
const TEXT_PADDING_TOP = 4;
const MAX_EMAIL_ATTACHMENT_BYTES = 30 * 1024 * 1024;
const DEFAULT_TICKET_EMAIL_PRIMARY_COLOR = "#4200ff";
const DEFAULT_TICKET_EMAIL_SECONDARY_COLOR = "#ff02c7";

function sanitizeEmailValue(value, fallback = "") {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return fallback;
}

function compactFilename(value, fallback = "ticket.pdf") {
  const normalized = sanitizeEmailValue(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 90);
  return normalized || fallback;
}

function buildTicketEmailTemplateParams({
  fromName,
  toName,
  toEmail,
  subject,
  title,
  message,
  headerImageUrl,
  primaryColor,
  secondaryColor,
  actionUrl,
  footerText,
  content,
  filename,
}) {
  const safeHeaderImageUrl = getEmailHeaderImageUrl(headerImageUrl);
  const safeActionUrl = sanitizeEmailValue(actionUrl, "https://comitty.eu");
  const safeFilename = compactFilename(filename);

  return {
    from_name: sanitizeEmailValue(fromName, "Comitty"),
    organization_name: sanitizeEmailValue(fromName, "Comitty"),
    to_name: sanitizeEmailValue(toName),
    to_email: sanitizeEmailValue(toEmail),
    email: sanitizeEmailValue(toEmail),
    subject: sanitizeEmailValue(subject, sanitizeEmailValue(fromName, "Comitty")),
    email_title: sanitizeEmailValue(title, sanitizeEmailValue(fromName, "Comitty")),
    title: sanitizeEmailValue(title, sanitizeEmailValue(fromName, "Comitty")),
    message: sanitizeEmailValue(message),
    header_image_url: safeHeaderImageUrl,
    header_display: safeHeaderImageUrl ? "block" : "none",
    primary_color: sanitizeEmailValue(primaryColor, DEFAULT_TICKET_EMAIL_PRIMARY_COLOR),
    secondary_color: sanitizeEmailValue(secondaryColor, DEFAULT_TICKET_EMAIL_SECONDARY_COLOR),
    action_url: safeActionUrl,
    success_url: safeActionUrl,
    footer_text: sanitizeEmailValue(footerText),
    content: sanitizeEmailValue(content),
    filename: safeFilename,
    attachment_name: safeFilename,
    attachment_filename: safeFilename,
  };
}

function getTicketName(ticket) {
  return ticket?.name || ticket?.title || ticket?.ticketName || "Ticket";
}

function formatTicketLabel(tickets = []) {
  if (!Array.isArray(tickets) || tickets.length === 0) return "Ticket";

  return tickets
    .map((ticket) => {
      const quantity = Number(ticket?.number) || 1;
      const ticketName = getTicketName(ticket);
      return quantity > 1 ? `${ticketName} x${quantity}` : ticketName;
    })
    .join(", ");
}

function fitTextToWidth(text, maxWidth) {
  if (!text) return "";
  const maxChars = Math.max(1, Math.floor(maxWidth / (TEXT_FONT * 0.55)));
  if (text.length <= maxChars) return text;
  if (maxChars <= 3) return text.slice(0, maxChars);
  return `${text.slice(0, maxChars - 3)}...`;
}

function isValidCustomTicketConfiguration(config) {
  return !!(
    config &&
    typeof config.x === "number" &&
    typeof config.y === "number" &&
    typeof config.size === "number" &&
    config.templatePath
  );
}

function getConfigurationKey(config) {
  if (!isValidCustomTicketConfiguration(config)) {
    return null;
  }

  return `${config.templatePath}::${config.x}::${config.y}::${config.size}`;
}

function resolveTicketConfiguration({ eventConfig, ticketConfig }) {
  if (isValidCustomTicketConfiguration(ticketConfig)) {
    return ticketConfig;
  }

  if (isValidCustomTicketConfiguration(eventConfig)) {
    return eventConfig;
  }

  return null;
}

async function getEventTicketCustomizationMap(eventId) {
  const ticketConfigsById = new Map();

  if (!eventId) {
    return ticketConfigsById;
  }

  try {
    const ticketsSnap = await getDocs(collection(db, "Events", eventId, "Tickets"));
    ticketsSnap.forEach((ticketDoc) => {
      const ticketData = ticketDoc.data();
      if (isValidCustomTicketConfiguration(ticketData?.customTicketConfiguration)) {
        ticketConfigsById.set(ticketDoc.id, ticketData.customTicketConfiguration);
      }
    });
  } catch (error) {
    console.error("Error fetching ticket custom configurations:", error);
  }

  return ticketConfigsById;
}

function pickSharedConfigurationForOrder({ participantTickets = [], eventConfig, ticketConfigMap }) {
  if (!Array.isArray(participantTickets) || participantTickets.length === 0) {
    return null;
  }

  const resolvedConfigs = participantTickets.map((ticket) =>
    resolveTicketConfiguration({
      eventConfig,
      ticketConfig: ticketConfigMap.get(ticket.ticketId),
    }),
  );

  const allTicketsHaveConfig = resolvedConfigs.every((config) => isValidCustomTicketConfiguration(config));
  if (!allTicketsHaveConfig) {
    return isValidCustomTicketConfiguration(eventConfig) ? eventConfig : null;
  }

  const uniqueConfigs = new Set(resolvedConfigs.map((config) => getConfigurationKey(config)));
  if (uniqueConfigs.size === 1) {
    return resolvedConfigs[0];
  }

  // A single order PDF cannot safely mix different templates without changing page generation flow.
  // In that case we fallback to the global template (if configured), otherwise default PDF renderer.
  return isValidCustomTicketConfiguration(eventConfig) ? eventConfig : null;
}

async function createPersonnalizeTicket({ qrCodesInfo }) {
  try {
    const pdfDoc = await PDFDocument.create();
    const templateCache = new Map();

    for (const qrCodeInfo of qrCodesInfo) {
      const currentConfig = qrCodeInfo.customTicketConfiguration;
      if (!isValidCustomTicketConfiguration(currentConfig)) {
        throw new Error("Missing valid custom ticket configuration for personalized ticket");
      }

      let cachedTemplate = templateCache.get(currentConfig.templatePath);

      if (!cachedTemplate) {
        const url = await getDownloadURL(ref(storage, currentConfig.templatePath));
        const templateResponse = await fetch(url);

        if (!templateResponse.ok) {
          throw new Error(
            `Failed to fetch PDF: ${templateResponse.status} ${templateResponse.statusText}`,
          );
        }

        const pdfArrayBuffer = await templateResponse.arrayBuffer();
        const [embeddedTemplatePage] = await pdfDoc.embedPdf(pdfArrayBuffer, [0]);
        cachedTemplate = {
          embeddedTemplatePage,
          templateWidth: embeddedTemplatePage.width,
          templateHeight: embeddedTemplatePage.height,
        };
        templateCache.set(currentConfig.templatePath, cachedTemplate);
      }

      const { embeddedTemplatePage, templateWidth, templateHeight } = cachedTemplate;
      const { x, y, size } = currentConfig;
      const page = pdfDoc.addPage([templateWidth, templateHeight]);
      page.drawPage(embeddedTemplatePage, {
        x: 0,
        y: 0,
        width: templateWidth,
        height: templateHeight,
      });

      const qrResponse = await fetch(qrCodeInfo.qrcode);
      const imgBytes = await qrResponse.arrayBuffer();
      const pngImage = await pdfDoc.embedPng(imgBytes);

      page.drawImage(pngImage, {
        x: x * templateWidth,
        y: templateHeight - size * templateWidth - y * templateHeight,
        width: size * templateWidth,
        height: size * templateWidth,
      });

      const rectY = templateHeight - size * templateWidth - y * templateHeight - TEXT_HEIGHT;

      page.drawRectangle({
        x: x * templateWidth,
        y: rectY,
        width: size * templateWidth,
        height: TEXT_HEIGHT,
        color: rgb(1, 1, 1),
      });

      const maxTextWidth = size * templateWidth - 8;
      const textLines = [fitTextToWidth(qrCodeInfo.id, maxTextWidth)];
      if (qrCodeInfo.ticketLabel) {
        textLines.push(fitTextToWidth(qrCodeInfo.ticketLabel, maxTextWidth));
      }

      textLines.forEach((line, lineIndex) => {
        const textWidth = TEXT_FONT * line.length * 0.55;
        const textX = x * templateWidth + (size * templateWidth - textWidth) / 2;
        const textY =
          rectY + TEXT_HEIGHT - TEXT_PADDING_TOP - TEXT_FONT - lineIndex * TEXT_LINE_HEIGHT;

        page.drawText(line, {
          x: textX,
          y: textY,
          size: TEXT_FONT,
          color: rgb(0, 0, 0),
        });
      });
    }

    const pdfBytesUpdated = await pdfDoc.save({ useObjectStreams: true });
    return new Blob([pdfBytesUpdated], { type: "application/pdf" });
  } catch (error) {
    console.error("Error creating personalized ticket:", error);
    throw error;
  }
}

function buildQrCodeInfo({ qrcode, participantId, tickets }) {
  return {
    qrcode,
    id: participantId,
    ticketLabel: formatTicketLabel(tickets),
  };
}

function getTranslatedDescription(event, lang) {
  const descriptions = event.descriptions;

  if (descriptions && typeof descriptions === "object" && Object.keys(descriptions).length > 0) {
    return descriptions[lang] || descriptions[Object.keys(descriptions)[0]];
  }

  return event.description || "";
}

function isTicketEmailCustomizationEnabled(event) {
  return Boolean(event?.ticketEmailCustomization?.enabled && ENV.emailJsTicketingTicketTemplateId);
}

function getEmailHeaderImageUrl(headerImageUrl) {
  const value = String(headerImageUrl || "").trim();
  return /^https?:\/\//i.test(value) ? value : "";
}

function buildTicketEmailParams({
  event,
  participantName,
  toEmail,
  content,
  message,
  successUrl = "",
  filename = "ticket.pdf",
}) {
  const customization = event?.ticketEmailCustomization || {};
  const eventName = sanitizeEmailValue(event?.name, "Comitty");
  const sanitizedMessage = sanitizeEmailValue(message);
  const customMessage = sanitizeEmailValue(customization.message);
  const mergedMessage = sanitizedMessage
    ? `${customMessage}${customMessage ? "\n\n" : ""}${sanitizedMessage}`
    : customMessage;
  const baseParams = buildTicketEmailTemplateParams({
    fromName: eventName,
    toName: participantName,
    toEmail,
    subject: customization.subject || eventName,
    title: customization.title || eventName,
    message: isTicketEmailCustomizationEnabled(event) ? mergedMessage : sanitizedMessage,
    headerImageUrl: customization.headerImageUrl,
    primaryColor: customization.primaryColor,
    secondaryColor: customization.secondaryColor,
    actionUrl: successUrl,
    footerText: customization.footerText,
    content,
    filename,
  });

  if (!isTicketEmailCustomizationEnabled(event)) {
    return {
      templateId: null,
      params: baseParams,
    };
  }

  return {
    templateId: ENV.emailJsTicketingTicketTemplateId,
    params: baseParams,
  };
}

async function splitPdfBlobByPages({ blob, maxBytes }) {
  const buffer = await blob.arrayBuffer();
  const sourcePdf = await PDFDocument.load(buffer);
  const pageCount = sourcePdf.getPageCount();

  if (pageCount === 0) {
    return [];
  }

  const buildChunk = async (start, end) => {
    const chunkPdf = await PDFDocument.create();
    const pageIndexes = Array.from({ length: end - start }, (_, i) => start + i);
    const copiedPages = await chunkPdf.copyPages(sourcePdf, pageIndexes);
    copiedPages.forEach((page) => chunkPdf.addPage(page));
    const chunkBytes = await chunkPdf.save({ useObjectStreams: true });
    return new Blob([chunkBytes], { type: "application/pdf" });
  };

  const splitRange = async (start, end) => {
    const candidate = await buildChunk(start, end);
    if (candidate.size <= maxBytes) {
      return [candidate];
    }

    if (end - start <= 1) {
      throw new Error("Ticket PDF too large even for a single page");
    }

    const middle = start + Math.ceil((end - start) / 2);
    const left = await splitRange(start, middle);
    const right = await splitRange(middle, end);
    return [...left, ...right];
  };

  return splitRange(0, pageCount);
}

async function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Failed to read generated ticket"));
    reader.readAsDataURL(blob);
  });
}

export async function handleCreateTicket({ participant, event, lang }) {
  const translatedDescription = getTranslatedDescription(event, lang);
  const eventCustomConfiguration = isValidCustomTicketConfiguration(event.customTicketConfiguration)
    ? event.customTicketConfiguration
    : null;
  const ticketConfigMap = await getEventTicketCustomizationMap(event.id);

  let blobPDF;
  if (participant.subdivision) {
    const qrcodes = [];
    const individualTickets = splitTicketsForPdf(participant.tickets);
    for (const index in individualTickets) {
      const qrcode = await QRCode.toDataURL(participant.id + index.toString(), { scale: 8 });
      qrcodes.push(
        {
          ...buildQrCodeInfo({
          qrcode,
          participantId: participant.id + index.toString(),
          tickets: [individualTickets[index]],
          }),
          customTicketConfiguration: resolveTicketConfiguration({
            eventConfig: eventCustomConfiguration,
            ticketConfig: ticketConfigMap.get(individualTickets[index].ticketId),
          }),
        },
      );
    }

    const canUsePersonalizedTicket = qrcodes.every((item) =>
      isValidCustomTicketConfiguration(item.customTicketConfiguration),
    );

    if (canUsePersonalizedTicket) {
      blobPDF = await createPersonnalizeTicket({
        qrCodesInfo: qrcodes,
      });
    } else {
      blobPDF = await pdf(
        TicketModel({
          qrCodesInfo: qrcodes,
          eventName: event.name,
          eventDescription: translatedDescription,
          tickets: individualTickets,
          details: participant.details,
          subdivision: true,
          lang: lang,
        }),
      ).toBlob();
    }
  } else {
    const qrcode = await QRCode.toDataURL(participant.id, { scale: 8 });
    const selectedConfiguration = pickSharedConfigurationForOrder({
      participantTickets: participant.tickets,
      eventConfig: eventCustomConfiguration,
      ticketConfigMap: ticketConfigMap,
    });

    if (isValidCustomTicketConfiguration(selectedConfiguration)) {
      blobPDF = await createPersonnalizeTicket({
        qrCodesInfo: [
          {
            ...buildQrCodeInfo({
              qrcode,
              participantId: participant.id,
              tickets: participant.tickets,
            }),
            customTicketConfiguration: selectedConfiguration,
          },
        ],
      });
    } else {
      blobPDF = await pdf(
        TicketModel({
          qrCodesInfo: { qrcode: qrcode, id: participant.id },
          participantId: participant.id,
          eventName: event.name,
          eventDescription: translatedDescription,
          tickets: participant.tickets,
          details: participant.details,
          lang: lang,
        }),
      ).toBlob();
    }
  }

  return blobPDF;
}

export async function downloadPDF({ ticket, participant, event, lang }) {
  let newBlobPDF = ticket;
  if (!newBlobPDF) {
    newBlobPDF = await handleCreateTicket({ participant, event, lang });
  }

  downloadPdfBlob(newBlobPDF);
}

export async function sendEmail({
  ticket,
  participant,
  event,
  lang,
  message,
  recipientEmail,
  successUrl = "",
  isPartner = false,
}) {
  let newBlobPDF = ticket;
  if (!newBlobPDF) {
    newBlobPDF = await handleCreateTicket({ participant, event, lang });
  }

  const participantName = participant.details.find((element) => element.name === "Firstname");
  const participantMail = participant.details.find((element) => element.name === "Email");
  const toEmail = String(recipientEmail || participantMail?.value || "").trim();
  if (!participantName?.value || !toEmail) {
    throw new Error("Missing participant name or email");
  }

  let ticketBlobs = [newBlobPDF];

  if (newBlobPDF.size > MAX_EMAIL_ATTACHMENT_BYTES) {
    ticketBlobs = await splitPdfBlobByPages({
      blob: newBlobPDF,
      maxBytes: MAX_EMAIL_ATTACHMENT_BYTES,
    });
  }

  for (let i = 0; i < ticketBlobs.length; i++) {
    const blob = ticketBlobs[i];
    const base64data = await blobToBase64(blob);
    const messageWithPart =
      ticketBlobs.length > 1
        ? `${message || ""}${message ? "\n\n" : ""}Part ${i + 1}/${ticketBlobs.length}`
        : message;

    if (isPartner) {
      const params = {
        from_name: event.name,
        to_name: participantName.value,
        email: toEmail,
        to_email: toEmail,
        content: base64data,
        message: messageWithPart,
      };
      await sendQRCode({ templateId: "template_xxwv6to", props: params });
      continue;
    }

    const { templateId, params } = buildTicketEmailParams({
      event,
      participantName: participantName.value,
      toEmail,
      content: base64data,
      message: messageWithPart,
      successUrl,
      filename:
        ticketBlobs.length > 1
          ? compactFilename(`${event?.name || "ticket"}-part-${i + 1}-${ticketBlobs.length}.pdf`)
          : compactFilename(`${event?.name || "ticket"}.pdf`),
    });
    await sendQRCode({ templateId, props: params });
  }
}
